import { useEffect, useRef, useState } from 'react'
import type { BlockDefinition, BlockTier } from '@tsagko/label-contracts'
import { useTranslation } from 'react-i18next'
import { CommitInput, CommitTextarea } from '../ui/CommitField'
import { validateBlockId } from './block-id'

interface BlockMetadataEditorProps {
  block: BlockDefinition
  onChange: (next: BlockDefinition) => void
  disabled?: boolean
  /**
   * When true the block already exists in storage (published or draft-only).
   * The id field becomes read-only and shows a copy button.
   * When false (new, unsaved block) the id is editable with inline validation.
   */
  existsInStorage?: boolean
  /** Ids already in use — only relevant when existsInStorage is false. */
  existingIds?: string[]
  /** Called whenever the id validation state changes (for the route to gate Save). */
  onIdValidChange?: (valid: boolean) => void
}

const CATEGORIES = ['measurement', 'temporal', 'commerce', 'contact', 'layout', 'compliance', 'custom']

/**
 * Editable id input for new (unsaved) blocks.
 * Keeps a local draft while typing; commits to the parent on blur or Enter.
 * Calls `onIdValidChange` on every keystroke so the route can gate Save.
 */
function NewBlockIdInput({
  value,
  existingIds,
  disabled,
  onCommit,
  onIdValidChange,
}: {
  value: string
  existingIds: string[]
  disabled?: boolean
  onCommit: (next: string) => void
  onIdValidChange?: (valid: boolean) => void
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState(value)
  const committedRef = useRef(false)

  // Re-seed when the canonical value changes (e.g. undo).
  useEffect(() => {
    setDraft(value)
    committedRef.current = false
  }, [value])

  const validation = validateBlockId(draft, existingIds)

  // Notify the route whenever validity changes — fires on mount, on typing,
  // on undo re-seeds, AND when the existing-ids list arrives after the user
  // already typed (a late-loading list can turn a valid id into a duplicate).
  useEffect(() => {
    onIdValidChange?.(validation.ok)
  }, [validation.ok, onIdValidChange])

  const commit = (next: string) => {
    if (committedRef.current) return
    committedRef.current = true
    if (next !== value) onCommit(next)
  }

  return (
    <>
      <input
        data-testid="bc-id-input"
        value={draft}
        disabled={disabled}
        className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 font-mono text-xs disabled:opacity-60 dark:border-stone-700 dark:bg-stone-900"
        onChange={e => {
          committedRef.current = false
          setDraft(e.target.value)
        }}
        onBlur={() => commit(draft)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            commit(draft)
            ;(e.currentTarget as HTMLInputElement).blur()
          }
        }}
      />
      {!validation.ok && (
        <p data-testid="bc-id-error" className="mt-1 text-[11px] text-rose-600">
          {t(validation.error.key, validation.error.params)}
        </p>
      )}
    </>
  )
}

export function BlockMetadataEditor({
  block,
  onChange,
  disabled,
  existsInStorage = false,
  existingIds = [],
  onIdValidChange,
}: BlockMetadataEditorProps) {
  const { t } = useTranslation()
  const patch = (changes: Partial<BlockDefinition>) => onChange({ ...block, ...changes })

  // Copy-button state: brief "Copied" feedback. The reset timer is cleared on
  // re-click (restart the window) and on unmount (no setState after unmount).
  const [copied, setCopied] = useState(false)
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => clearTimeout(copiedTimerRef.current), [])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(block.id)
    } catch {
      // Fallback: create a transient textarea (works in non-secure contexts).
      const el = document.createElement('textarea')
      el.value = block.id
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
    }
    setCopied(true)
    clearTimeout(copiedTimerRef.current)
    copiedTimerRef.current = setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div
      data-testid="bc-metadata"
      className="space-y-3 border-b border-stone-200 px-4 py-4 dark:border-stone-800"
    >
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
          {t('blockConfigurator.metadata.name')}
        </label>
        <CommitInput
          data-testid="bc-name"
          value={block.name}
          onCommit={name => patch({ name })}
          disabled={disabled}
          className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 text-sm disabled:opacity-60 dark:border-stone-700 dark:bg-stone-900"
        />
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
          {t('blockConfigurator.metadata.id')}
        </label>
        {existsInStorage ? (
          // Existing block: read-only monospace display + copy button.
          <div className="mt-1 flex items-center gap-1">
            <span
              data-testid="bc-id-display"
              className="flex-1 rounded border border-stone-200 bg-stone-50 px-2 py-1 font-mono text-xs dark:border-stone-700 dark:bg-stone-900/50"
            >
              {block.id}
            </span>
            <button
              type="button"
              data-testid="bc-id-copy"
              onClick={handleCopy}
              title={t('blockConfigurator.metadata.copyId')}
              className="shrink-0 rounded border border-stone-300 px-2 py-1 text-[11px] hover:bg-stone-100 dark:border-stone-700 dark:hover:bg-stone-800"
            >
              {copied ? t('blockConfigurator.metadata.copied') : t('blockConfigurator.metadata.copy')}
            </button>
          </div>
        ) : (
          // New unsaved block: editable with live validation.
          <NewBlockIdInput
            value={block.id}
            existingIds={existingIds}
            disabled={disabled}
            onCommit={id => patch({ id })}
            onIdValidChange={onIdValidChange}
          />
        )}
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
          {t('blockConfigurator.metadata.tier')}
        </label>
        <select
          value={block.tier}
          onChange={e => patch({ tier: e.target.value as BlockTier })}
          disabled={disabled}
          className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 text-xs disabled:opacity-60 dark:border-stone-700 dark:bg-stone-900"
        >
          {/* Only the OPTION TEXT is translated — the `value` stays the raw
              BlockTier the contract stores. */}
          <option value="custom">{t('tier.custom')}</option>
          <option value="platform">{t('tier.platform')}</option>
        </select>
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
          {t('blockConfigurator.metadata.category')}
        </label>
        <select
          value={block.category ?? ''}
          onChange={e => patch({ category: e.target.value || undefined })}
          disabled={disabled}
          className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 text-xs disabled:opacity-60 dark:border-stone-700 dark:bg-stone-900"
        >
          <option value="">{t('blockConfigurator.metadata.categoryNone')}</option>
          {CATEGORIES.map(c => (
            <option key={c} value={c}>
              {t(`category.${c}`, { defaultValue: c })}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
          {t('blockConfigurator.metadata.icon')}
        </label>
        <input
          value={block.icon ?? ''}
          onChange={e => patch({ icon: e.target.value || undefined })}
          placeholder={t('blockConfigurator.metadata.iconPlaceholder')}
          disabled={disabled}
          className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 text-xs disabled:opacity-60 dark:border-stone-700 dark:bg-stone-900"
        />
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
          {t('blockConfigurator.metadata.description')}
        </label>
        <CommitTextarea
          data-testid="bc-description"
          value={block.description ?? ''}
          onCommit={v => patch({ description: v || undefined })}
          rows={2}
          disabled={disabled}
          className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 text-xs disabled:opacity-60 dark:border-stone-700 dark:bg-stone-900"
        />
      </div>
    </div>
  )
}
